/**
 * Expand People Group references into individual member labels. A token that
 * matches a group name (case-insensitive, with an optional leading `@`) is
 * replaced by that group's members; everything else passes through unchanged.
 * Used to turn a parallel-approval roster or a notification recipient list that
 * names a group into the concrete people.
 */
import type { PeopleGroup } from '@/types/people-group'

/** Read a `form.<key>` style token from the run dimensions. The bag is nested
 *  (`{ form: { key } }`, see getDimensions), so walk the dotted path; a flat
 *  `{ 'form.key': v }` bag is honored too. */
export function resolveDimension(path: string, dims?: Record<string, unknown>): unknown {
  if (!dims) return undefined
  if (path in dims) return dims[path]
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object' && !Array.isArray(acc)) return (acc as Record<string, unknown>)[k.trim()]
    return undefined
  }, dims)
}

/** A routable text for one value: strings and numbers as-is, a directory
 *  person reference as its staff name, anything else nothing. */
function personText(x: unknown): string | null {
  if (x == null) return null
  if (typeof x !== 'object') return String(x)
  const ref = x as { kind?: unknown; name?: unknown }
  return ref.kind === 'directory_person' && typeof ref.name === 'string' ? ref.name : null
}

function groupIndex(groups: PeopleGroup[]): Map<string, PeopleGroup> {
  const m = new Map<string, PeopleGroup>()
  for (const g of groups) m.set(g.name.trim().toLowerCase(), g)
  return m
}

/**
 * Expand a list of assignee/approver tokens.
 *  - `@Group`      resolves to the group's members.
 *  - `form.<key>`  is DYNAMIC assignment: it resolves to the value that field
 *                  held on the submitted form (passed in `dims`, e.g. the person
 *                  named in the "Program Manager" field). The resolved value is
 *                  itself expanded once (so a form field that points at a group
 *                  still expands). If the form value is not available yet, the
 *                  token is kept as-is so it stays visible and can resolve later.
 *  - `requester.<attribute>` is the same kind of dynamic reference, read from
 *                  the requester profile startRun snapshots onto the run
 *                  (services/directory/personnel.ts), e.g. `requester.manager`.
 *  - A people field set to the live directory stores { kind:
 *    'directory_person', name, ... }; it resolves to the person's staff name.
 *  - anything else passes through unchanged.
 */
export function expandTokens(tokens: string[], groups: PeopleGroup[], dims?: Record<string, unknown>): string[] {
  const byName = groupIndex(groups)
  const out: string[] = []
  for (const raw of tokens) {
    const t = raw.trim()
    if (!t) continue
    if (/^(form|requester)\./i.test(t)) {
      const v = resolveDimension(t.replace(/^requester\./i, 'requester.'), dims)
      const parts = (Array.isArray(v) ? v : [v]).map(personText).filter((x): x is string => x != null)
      if (parts.some((x) => x.trim())) {
        // Resolve the referenced field's value (it may itself be a user or @group).
        // A bulk request exposes a row field as the array of its distinct values.
        const inner = parts.flatMap((x) => x.split(',')).map((x) => x.trim()).filter(Boolean)
        for (const iv of inner) {
          const g = byName.get(iv.replace(/^@/, '').trim().toLowerCase())
          if (g && g.members.length) out.push(...g.members.map((m) => m.trim()).filter(Boolean))
          else out.push(iv)
        }
      } else {
        out.push(t) // unresolved dynamic ref; keep visible
      }
      continue
    }
    const key = t.replace(/^@/, '').trim().toLowerCase()
    const g = byName.get(key)
    if (g && g.members.length) out.push(...g.members.map((m) => m.trim()).filter(Boolean))
    else out.push(t)
  }
  return [...new Set(out)]
}

/** Expand a single assignee string that may reference a group (returns member
 *  list, or the original as a one-item list if it isn't a group). */
export function expandOne(token: string | null | undefined, groups: PeopleGroup[]): string[] {
  if (!token) return []
  return expandTokens([token], groups)
}

// ---------------------------------------------------------------------------
// Empty-group detection (loud handling)
// ---------------------------------------------------------------------------
//
// An empty group is a real governance gap: expandTokens keeps the raw token
// when the named group has no members, so the gate pauses with no concrete
// approver and the inbox shows it to whoever looks. The pause behavior is
// deliberate; these helpers exist so every surface can SAY so instead of
// failing silently. Single source of truth: the MDG console, the Unified
// Inbox, the Run Monitor, and the Workflow Studio list all route through here.

/** Group names referenced by `tokens` (with or without a leading `@`) that
 *  exist but have no members. Order preserved, de-duplicated by group name. */
export function emptyGroupRefs(tokens: Array<string | null | undefined>, groups: PeopleGroup[]): string[] {
  const byName = groupIndex(groups)
  const out: string[] = []
  for (const raw of tokens) {
    const t = (raw ?? '').trim()
    if (!t || /^(form|requester)\./i.test(t)) continue
    const g = byName.get(t.replace(/^@/, '').trim().toLowerCase())
    if (g && g.members.filter((m) => m.trim()).length === 0 && !out.includes(g.name)) out.push(g.name)
  }
  return out
}

/** Empty groups a runtime plan step still points at (approvers roster,
 *  assignee, or the raw `inputs.approvers` string). */
export function planStepEmptyGroups(
  step: { assignee?: string | null; approvers?: string[]; inputs?: Record<string, unknown> },
  groups: PeopleGroup[],
): string[] {
  const tokens: Array<string | null | undefined> = [
    ...(step.approvers ?? []),
    step.assignee,
    ...String(step.inputs?.approvers ?? '').split(',').map((x) => x.trim()),
  ]
  return emptyGroupRefs(tokens, groups)
}

/** Empty groups a workflow SPEC routes to: scans approval / parallel / manual
 *  steps' approver rosters and assignees. Drives the workflow-level warning
 *  chips in the Studio list and the MDG console. */
export function specEmptyGroups(
  spec: { steps: Array<{ type?: string; assignee?: string | null; inputs?: Record<string, unknown> }> },
  groups: PeopleGroup[],
): string[] {
  const out: string[] = []
  for (const s of spec.steps) {
    if (s.type && !['approval', 'parallel', 'manual', 'notification'].includes(s.type)) continue
    const names = planStepEmptyGroups({ assignee: s.assignee, inputs: s.inputs }, groups)
    for (const n of names) if (!out.includes(n)) out.push(n)
  }
  return out
}

/** The standard warning sentence every surface shows for an unassigned gate. */
export function emptyGroupWarningText(names: string[]): string {
  const label = names.join(', ')
  return `Approval group ${label} has no members. Anyone can act on this gate until members are added in People Groups.`
}
